import { useEffect, useMemo, useRef } from 'react';
import type { RefObject } from 'react';
import type { StoryModel } from './story-model';
import { activePeriods } from './story-model';
import { drawTimeWheel } from './draw-time-wheel';
import { formatStoryDateTime, periodAccentRgb } from './story-dom';
import { buildPeriodTrackLayout } from './story-period-tracks';
import type { TimelinePositionEngine } from './use-timeline-position';

interface UseTimeWheelCanvasArgs {
  readonly model: StoryModel;
  readonly engine: TimelinePositionEngine;
}

export function useTimeWheelCanvas({ model, engine }: UseTimeWheelCanvasArgs) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const currentDateRef = useRef<HTMLSpanElement>(null);
  const currentPeriodsRef = useRef<HTMLSpanElement>(null);
  const currentPeriodBadgeRef = useRef<HTMLSpanElement>(null);
  const stateRef = useRef(createWheelState(model, engine.get()));
  const periodLayout = useMemo(() => buildPeriodTrackLayout(model.periods), [model.periods]);

  useEffect(() => {
    stateRef.current.model = model;
    stateRef.current.periodTracks = periodLayout;
    writeWheelText(stateRef.current, currentDateRef, currentPeriodsRef, currentPeriodBadgeRef);
    scheduleDraw(canvasRef, stateRef);
  }, [model, periodLayout]);

  useEffect(() => setupReducedMotion(canvasRef, stateRef), []);
  useEffect(() => setupResizeObserver(wrapperRef, canvasRef, stateRef), []);
  useEffect(
    () =>
      setupEngineSubscription(
        engine,
        canvasRef,
        stateRef,
        currentDateRef,
        currentPeriodsRef,
        currentPeriodBadgeRef,
      ),
    [engine],
  );

  return { canvasRef, wrapperRef, currentDateRef, currentPeriodsRef, currentPeriodBadgeRef };
}

interface WheelState {
  model: StoryModel;
  periodTracks: ReturnType<typeof buildPeriodTrackLayout>;
  position: number;
  reducedMotion: boolean;
  frameId: number | null;
  width: number;
  height: number;
  dpr: number;
}

function createWheelState(model: StoryModel, position: number): WheelState {
  return {
    model,
    periodTracks: buildPeriodTrackLayout(model.periods),
    position,
    reducedMotion: false,
    frameId: null,
    width: 0,
    height: 0,
    dpr: 1,
  };
}

function setupReducedMotion(
  canvasRef: RefObject<HTMLCanvasElement>,
  stateRef: RefObject<WheelState>,
) {
  const query = window.matchMedia('(prefers-reduced-motion: reduce)');
  const update = () => {
    if (!stateRef.current) return;
    stateRef.current.reducedMotion = query.matches;
    scheduleDraw(canvasRef, stateRef);
  };
  update();
  query.addEventListener('change', update);
  return () => query.removeEventListener('change', update);
}

function setupResizeObserver(
  wrapperRef: RefObject<HTMLDivElement>,
  canvasRef: RefObject<HTMLCanvasElement>,
  stateRef: RefObject<WheelState>,
) {
  const wrapper = wrapperRef.current;
  if (!wrapper) return undefined;
  const resize = () => {
    const state = stateRef.current;
    const canvas = canvasRef.current;
    if (!state || !canvas) return;
    const width = Math.max(0, Math.floor(wrapper.clientWidth));
    const height = Math.max(0, Math.floor(wrapper.clientHeight));
    state.width = width;
    state.height = height;
    state.dpr = Math.max(1, window.devicePixelRatio || 1);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.width = Math.max(1, Math.floor(width * state.dpr));
    canvas.height = Math.max(1, Math.floor(height * state.dpr));
    scheduleDraw(canvasRef, stateRef);
  };
  const observer = new ResizeObserver(resize);
  observer.observe(wrapper);
  resize();
  return () => observer.disconnect();
}

function setupEngineSubscription(
  engine: TimelinePositionEngine,
  canvasRef: RefObject<HTMLCanvasElement>,
  stateRef: RefObject<WheelState>,
  currentDateRef: RefObject<HTMLSpanElement>,
  currentPeriodsRef: RefObject<HTMLSpanElement>,
  currentPeriodBadgeRef: RefObject<HTMLSpanElement>,
) {
  const renderText = () => {
    const state = stateRef.current;
    if (!state) return;
    writeWheelText(state, currentDateRef, currentPeriodsRef, currentPeriodBadgeRef);
  };
  const unsubscribe = engine.subscribe((position) => {
    if (!stateRef.current) return;
    stateRef.current.position = position;
    renderText();
    scheduleDraw(canvasRef, stateRef);
  });
  renderText();
  scheduleDraw(canvasRef, stateRef);
  return unsubscribe;
}

function scheduleDraw(
  canvasRef: RefObject<HTMLCanvasElement>,
  stateRef: RefObject<WheelState>,
): void {
  const state = stateRef.current;
  if (!state || state.frameId !== null) return;
  state.frameId = requestAnimationFrame(() => {
    const nextState = stateRef.current;
    if (!nextState) return;
    nextState.frameId = null;
    paintWheel(canvasRef, nextState);
  });
}

function paintWheel(canvasRef: RefObject<HTMLCanvasElement>, state: WheelState): void {
  const canvas = canvasRef.current;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  drawTimeWheel(ctx, {
    model: state.model,
    position: state.position,
    width: state.width,
    height: state.height,
    dpr: state.dpr,
    periodTracks: state.periodTracks,
    reducedMotion: state.reducedMotion,
  });
}

function writeWheelText(
  state: WheelState,
  currentDateRef: RefObject<HTMLSpanElement>,
  currentPeriodsRef: RefObject<HTMLSpanElement>,
  currentPeriodBadgeRef: RefObject<HTMLSpanElement>,
): void {
  const epoch = state.model.scale.toTime(state.position);
  if (currentDateRef.current) currentDateRef.current.textContent = formatStoryDateTime(epoch);
  const active = activePeriods(state.model, state.position);
  const names = active.map((period) => period.period.label);
  const content = names.length === 0 ? 'Ingen aktiv periode' : names.join(' · ');
  if (currentPeriodsRef.current) currentPeriodsRef.current.textContent = content;
  if (!currentPeriodBadgeRef.current) return;
  currentPeriodBadgeRef.current.textContent = names.length === 0 ? '' : names.join(' · ');
  stylePeriodBadge(
    currentPeriodBadgeRef.current,
    active.map((period) => period.period.color),
    names.length === 0,
  );
}

function stylePeriodBadge(
  node: HTMLSpanElement,
  colors: readonly string[],
  isFallback: boolean,
): void {
  if (isFallback || colors.length === 0) {
    node.style.display = 'none';
    node.style.backgroundColor = '';
    node.style.borderColor = '';
    node.style.color = '';
    return;
  }
  node.style.display = 'inline-flex';
  const [r, g, b] = periodAccentRgb(colors);
  node.style.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.18)`;
  node.style.borderColor = `rgba(${r}, ${g}, ${b}, 0.56)`;
  node.style.color = `rgb(${r}, ${g}, ${b})`;
}
